import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { BiX } from 'react-icons/bi';
import * as styles from './styles';
import { modalStyles } from './CreateInvoiceGroupModal';
import FindInvoicesModal from './FindInvoicesModal';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';

interface IPostInvoice {
    leaseNo: number;
    invoiceNo: string;
    invoiceDate: string;
    dueDate1?: string;
    clNo: number;
    prNo: number;
    scNo: number;
    invoiceAmount: number;
    liTg: number;
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    lsNo: number;
    onSuccess: () => void;
}

interface ISearchCriteria {
    clientNo: string;
    prospectNo: string;
    sectionNo: string;
}

const PostInvoicesModal: React.FC<IProps> = ({ isOpen, onClose, lsNo, onSuccess }) => {
    const { get, post, response, loading } = useFetch();

    // --- State ---
    const [invoices, setInvoices] = useState<IPostInvoice[]>([]);
    const [selectedLiTgs, setSelectedLiTgs] = useState<number[]>([]);

    const [showFindModal, setShowFindModal] = useState(false);
    const [activeCriteria, setActiveCriteria] = useState<ISearchCriteria | null>(null);
    const [workflowError, setWorkflowError] = useState<ILeaseRecordsApiError | null>(null);

    // --- Initial Load ---
    useEffect(() => {
        if (isOpen && lsNo) {
            // Only load initial data if we haven't already populated via search/state preservation
            // But typically for a fresh open, we reset.
            loadInitialData();
        }
        // Reload only when the modal opens or its lease changes. use-http does not
        // guarantee stable method/response identities between renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, lsNo]);

    const loadInitialData = async () => {
        const data = await get(`/v1.0/leases/invoices/to-post/${lsNo}`);
        if (response.ok) {
            setInvoices(data || []);
            setSelectedLiTgs([]); // Clear selection on fresh load
        }
    };

    // --- Handlers ---

    const handleFind = async (criteria: ISearchCriteria) => {
        const payload = {
            clientNo: criteria.clientNo ? parseInt(criteria.clientNo, 10) : null,
            prospectNo: criteria.prospectNo ? parseInt(criteria.prospectNo, 10) : null,
            sectionNo: criteria.sectionNo ? parseInt(criteria.sectionNo, 10) : null,
        };

        if (!payload.clientNo && !payload.prospectNo && !payload.sectionNo) {
            return;
        }

        setActiveCriteria(criteria);

        const newInvoices = await post(`/v1.0/leases/invoices/search-to-post`, payload);

        if (response.ok && Array.isArray(newInvoices)) {
            setInvoices((prevInvoices) => {
                // Senior Level Deduplication:
                // Create a Set of existing IDs for O(1) lookup
                const existingIds = new Set(prevInvoices.map((inv) => inv.liTg));

                // Filter new items that don't exist in the current list
                const uniqueNewInvoices = newInvoices.filter((inv) => !existingIds.has(inv.liTg));

                // Return new array with appended unique items
                return [...prevInvoices, ...uniqueNewInvoices];
            });
            // Note: We deliberately DO NOT clear selectedLiTgs here so user keeps previous selections
        }
    };

    const handlePost = async () => {
        if (selectedLiTgs.length === 0) {
            setWorkflowError(parseLeaseRecordsApiError({ title: 'Select at least one invoice to post.' }));
            return;
        }

        const has59InvoiceWithoutDraftDueDate = invoices.some(
            (invoice) => selectedLiTgs.includes(invoice.liTg) && invoice.leaseNo.toString().startsWith('59') && !invoice.dueDate1
        );
        if (has59InvoiceWithoutDraftDueDate) {
            setWorkflowError(parseLeaseRecordsApiError({ title: 'Draft Due Date is required before posting an invoice for a 59 lease.' }));
            return;
        }

        setWorkflowError(null);
        const result = await post('/v1.0/leases/invoices/post', { selectedLiTgs });

        if (response.ok) {
            onSuccess();
            onClose();
        } else {
            setWorkflowError(parseLeaseRecordsApiError(result || response.data, response.status));
        }
    };

    // --- Selection Logic ---

    const toggleRow = (liTg: number) => {
        setSelectedLiTgs((prev) => (prev.includes(liTg) ? prev.filter((id) => id !== liTg) : [...prev, liTg]));
    };

    const handleSelectAll = () => setSelectedLiTgs(invoices.map((i) => i.liTg));
    const handleDeselectAll = () => setSelectedLiTgs([]);

    // --- Helpers ---

    const getCriteriaString = () => {
        if (!activeCriteria) {
            return '';
        }
        return [
            activeCriteria.clientNo && `Client: ${activeCriteria.clientNo}`,
            activeCriteria.prospectNo && `Prospect: ${activeCriteria.prospectNo}`,
            activeCriteria.sectionNo && `Section: ${activeCriteria.sectionNo}`,
        ]
            .filter(Boolean)
            .join(', ');
    };

    const formatMoney = (val?: number) => (val ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '$0.00');
    const formatDate = (val?: string) => (val ? new Date(val).toLocaleDateString() : '');
    const selectedInvoiceTotal = invoices.reduce(
        (total, invoice) => (selectedLiTgs.includes(invoice.liTg) ? total + (invoice.invoiceAmount || 0) : total),
        0
    );

    if (!isOpen) {
        return null;
    }

    return (
        <div style={modalStyles.modalOverlay}>
            <div style={{ ...modalStyles.modalContent, width: '1000px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <div style={modalStyles.modalHeader}>
                    <h3 style={modalStyles.modalTitle}>Post Lease Invoices</h3>
                    <button onClick={onClose} style={modalStyles.iconButton}>
                        <BiX size={24} />
                    </button>
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflow: 'auto', position: 'relative', borderBottom: `1px solid ${styles.colors.border}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead
                            style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: styles.colors.gray100,
                                zIndex: 2,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            }}
                        >
                            <tr>
                                <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                                    <LeaseRecordInput
                                        type="checkbox"
                                        checked={invoices.length > 0 && selectedLiTgs.length === invoices.length}
                                        onChange={(e) => (e.target.checked ? handleSelectAll() : handleDeselectAll())}
                                    />
                                </th>
                                <th style={thStyle}>Lease No.</th>
                                <th style={thStyle}>Invoice No.</th>
                                <th style={thStyle}>Invoice Date</th>
                                <th style={thStyle}>Prospect No.</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Invoice Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: styles.colors.gray500 }}>
                                        {activeCriteria ? 'No invoices found matching criteria.' : 'No invoices available to post.'}
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((r, i) => {
                                    const isSelected = selectedLiTgs.includes(r.liTg);
                                    return (
                                        <tr
                                            key={r.liTg}
                                            style={{
                                                borderBottom: `1px solid ${styles.colors.border}`,
                                                backgroundColor: isSelected ? '#EFF6FF' : i % 2 === 0 ? '#ffffff' : '#F9FAFB',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => toggleRow(r.liTg)}
                                        >
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <LeaseRecordInput
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleRow(r.liTg)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </td>
                                            <td style={tdStyle}>{r.leaseNo}</td>
                                            <td style={tdStyle}>{r.invoiceNo}</td>
                                            <td style={tdStyle}>{formatDate(r.invoiceDate)}</td>
                                            <td style={tdStyle}>
                                                {r.clNo}-{r.prNo}
                                                {r.scNo ? `-${r.scNo}` : ''}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{formatMoney(r.invoiceAmount)}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Buttons */}
                <LeaseRecordsErrorAlert error={workflowError} onDismiss={() => setWorkflowError(null)} />
                <div
                    style={{
                        padding: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        borderTop: `1px solid ${styles.colors.border}`,
                    }}
                >
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <button onClick={() => setShowFindModal(true)} style={styles.buttonSecondary}>
                            Find
                        </button>

                        {/* Search Feedback / Select Buttons */}
                        {activeCriteria ? (
                            <span style={{ marginLeft: '1rem', fontSize: '0.9rem', color: '#4B5563', fontStyle: 'italic' }}>
                                Appended results for: <strong>{getCriteriaString()}</strong>
                            </span>
                        ) : (
                            <>
                                <button onClick={handleSelectAll} style={styles.buttonSecondary}>
                                    Select All
                                </button>
                                <button onClick={handleDeselectAll} style={styles.buttonSecondary}>
                                    Deselect All
                                </button>
                            </>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: 600, color: '#374151' }}>Invoice Total</span>
                            <span
                                style={{
                                    minWidth: '110px',
                                    padding: '0.5rem 0.75rem',
                                    backgroundColor: '#10B981',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    fontWeight: 700,
                                    textAlign: 'right',
                                }}
                            >
                                {formatMoney(selectedInvoiceTotal)}
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                                onClick={handlePost}
                                style={{ ...styles.buttonPrimary, minWidth: '80px' }}
                                disabled={loading || selectedLiTgs.length === 0}
                            >
                                {loading ? 'Posting...' : 'Post'}
                            </button>
                            <button onClick={onClose} style={styles.buttonSecondary}>
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <FindInvoicesModal isOpen={showFindModal} onClose={() => setShowFindModal(false)} onFind={handleFind} />
        </div>
    );
};

// --- Local Styles ---
const thStyle: React.CSSProperties = {
    padding: '0.75rem',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '1px solid #D1D5DB',
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
    padding: '0.6rem 0.75rem',
    color: '#111827',
    whiteSpace: 'nowrap',
};

export default PostInvoicesModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/PostInvoicesModal.tsx